import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { isMobile } from 'react-device-detect';

import { useUserSelector } from 'hooks/useUser';

import Logo from '@ui/components/Logo';
import Icon from '@ui/components/Icon';
import { Label } from '@ui/components/Typography';

import UserAvatar from 'components/UserAvatar';
import { usePassport } from 'hooks/usePassport';
import { usePostHogFlag } from 'hooks/usePostHogFlag';
import { isCountrySupported } from 'pages/country-services/config';

const VERIFY_EURO_ACCOUNT_PAGE_FLAG = 'Verify-euro-account-page';

const Sidebar = styled.div`
  position: fixed;
  min-width: 300px;
  max-width: 300px;
  overflow: auto;
  padding: 16px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-right: 1px solid var(--grey-20);
  background-color: var(--white);
  filter: ${(props) => (props.$blurred ? 'blur(6px)' : 'none')};

  @media (max-width: 874px) {
    min-width: 100%;
    flex-direction: row;
    justify-content: space-around;
    align-items: center;
    bottom: 0;
    border-top: 1px solid var(--grey-30);
    background: var(--white);
    padding: 16px 8px;
    /* Fallback for older iOS (constant) then modern standard (env) */
    padding-bottom: calc(16px + constant(safe-area-inset-bottom));
    padding-bottom: calc(16px + env(safe-area-inset-bottom));
    min-height: 73px;
    height: auto;
    z-index: 1000;
    position: fixed;
    left: 0;
    right: 0;
    /* On iOS Safari, position: fixed with bottom: 0 sticks to visual viewport */
    /* The safe-area-inset-bottom in padding handles the home indicator area */

    &::after {
      content: '';
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      /* Fallback for older iOS (constant) then modern standard (env) */
      height: constant(safe-area-inset-bottom);
      height: env(safe-area-inset-bottom);
      background: var(--white);
      z-index: 8;
      pointer-events: none;
    }
  }
`;

const LogoWrapper = styled.div`
  margin: 8px 8px 0 8px;

  @media (max-width: 874px) {
    display: none;
  }
`;

const Navbar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;

  @media (max-width: 874px) {
    width: 100%;
  }
`;

const NavbarItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0 8px;

  @media (max-width: 874px) {
    flex-direction: row;
    gap: normal;
    width: 100%;
    justify-content: space-around;
  }
`;

const NavbarSubItemGroup = styled.div`
  margin-left: 24px;
`;

const CollapsibleHeader = styled.div`
  display: flex;
  gap: 16px;
  margin: 4px;
  padding: 8px 12px;
  border-radius: 12px;
  cursor: pointer;
  align-items: center;
  justify-content: space-between;

  @media (min-width: 875px) {
    &:hover {
      background-color: var(--grey-20);
    }

    ${(props) => (props.active ? 'background-color: var(--grey-20);' : '')};
  }
`;

const CollapsibleHeaderContent = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const CollapseIcon = styled.div`
  transition: transform 0.2s ease;
  transform: ${(props) => (props.$expanded ? 'rotate(180deg)' : 'rotate(0deg)')};
  display: flex;
  align-items: center;
`;

const SectionHeader = styled.div`
  display: flex;
  gap: 16px;
  margin: 4px;
  padding: 8px 12px;
  align-items: center;
  cursor: default;
`;

const PropertyFinderWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const NavbarItem = styled.button`
  display: flex;
  gap: 16px;
  margin: 4px;
  padding: 8px 12px;
  width: calc(100% - 8px);
  border: 0;
  border-radius: 12px;
  cursor: pointer;
  align-items: center;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;

  @media (min-width: 875px) {
    &:hover {
      background-color: var(--grey-20);
    }

    ${(props) => (props.active ? 'background-color: var(--grey-20);' : '')};
  }

  @media (max-width: 874px) {
    width: auto;
    flex-direction: column;
    gap: 4px;
    align-items: center;
    background-color: inherit;
    padding: 0;
    margin: 0;
    position: relative;

    ${(props) =>
      props.active
        ? `
      &::after {
        content: '';
        position: absolute;
        bottom: -16px;
        left: 50%;
        transform: translateX(-50%);
        width: 40px;
        height: 3px;
        background-color: #2980b9;
        border-radius: 3px 3px 0 0;
      }

      label {
        color: #2980b9 !important;
      }
    `
        : ''};
  }
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-top: 120px;

  @media (max-width: 874px) {
    display: none;
  }
`;

const UserDetails = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const UserDetailsAction = styled.div`
  display: flex;
  gap: 10px;
  cursor: pointer;
  padding: 16px 8px;
  align-items: center;
`;

const UserPersonalData = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  text-align: left;
  max-width: 140px;
`;

const LogoutWrapper = styled.div`
  cursor: pointer;
`;

const MobileMoreMenu = styled.div`
  position: fixed;
  bottom: 73px;
  left: 0;
  right: 0;
  background: var(--white);
  border-top: 1px solid var(--grey-30);
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1001;
  max-height: 70vh;
  overflow-y: auto;
  padding: 16px 8px;
  display: ${(props) => (props.$isVisible ? 'block' : 'none')};
`;

const MobileMoreMenuItem = styled.button`
  display: flex;
  gap: 16px;
  width: 100%;
  padding: 12px 16px;
  border: 0;
  border-radius: 12px;
  cursor: pointer;
  align-items: center;
  color: inherit;
  font: inherit;
  text-align: left;
  background-color: ${(props) => (props.active ? 'var(--grey-20)' : 'transparent')};

  &:hover {
    background-color: var(--grey-20);
  }
`;

const MobileMoreMenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 73px;
  background: rgba(0, 0, 0, 0.3);
  z-index: 999;
  display: ${(props) => (props.$isVisible ? 'block' : 'none')};
`;

const PngIcon = styled.img`
  width: ${(props) => props.size || 24}px;
  height: ${(props) => props.size || 24}px;
  object-fit: contain;
`;

export const PageContentWrapper = styled.div`
  padding: 62px;

  @media (max-width: 874px) {
    padding: 12px;
  }
`;

export const PageHeaderWrapper = styled.div`
  padding: 32px 62px;

  @media (max-width: 874px) {
    padding: 6px 12px;
    margin-bottom: 32px;
  }
`;

const SidebarContent = ({ blurred = false }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { userEmail, userName, user, isPropertyFinderUser } = useUserSelector();
  const navigate = useNavigate();
  const { clearJwtTokens } = useUserSelector();
  const { docusignDocuments, stage2Submitted, mortgageCountry, isPassportRequestsLoading } =
    usePassport();
  const isVerifyEuroAccountPageReady = !isPassportRequestsLoading && !!mortgageCountry;
  const isVerifyEuroAccountPageEnabled = usePostHogFlag(VERIFY_EURO_ACCOUNT_PAGE_FLAG, {
    ready: isVerifyEuroAccountPageReady
  });
  const [mobileMoreMenuVisible, setMobileMoreMenuVisible] = useState(false);
  const [propertyFinderExpanded, setPropertyFinderExpanded] = useState(
    isPropertyFinderUser || pathname.startsWith('/app/property-finder')
  );
  const passportCountry = user?.passport_data?.property_country;
  const passportCountryCode = useMemo(() => {
    if (stage2Submitted) {
      return passportCountry.country_code;
    }
    return '';
  }, [stage2Submitted, passportCountry]);

  const onLogout = useCallback(async () => {
    function clearAllCookies() {
      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        const eqPos = cookie.indexOf('=');
        const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
        document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
      }
    }
    clearJwtTokens();
    clearAllCookies();
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/login';
  }, [clearJwtTokens]);

  return (
    <>
      <Sidebar $blurred={blurred}>
        <Navbar>
          <LogoWrapper>
            <Logo />
          </LogoWrapper>
          <NavbarItems>
            <>
              {!isMobile ? (
                <>
                  <SectionHeader>
                    <Icon name="flight--international" size="24" />
                    <Label variant="label-m" style={{ fontWeight: 600 }}>
                      {t('Finance Passport')}
                    </Label>
                    {passportCountryCode ? (
                      <img
                        src={`/images/original/flags/${passportCountryCode?.toUpperCase()}.png`}
                        alt={passportCountryCode}
                        height="14px"
                      />
                    ) : null}
                  </SectionHeader>

                  <NavbarItem
                    onClick={() => navigate(`/passport/${passportCountryCode}#offers`)}
                    active={false}
                  >
                    <Icon name="mortgage" size="24" />
                    <Label variant="label-m">{t('Mortgages')}</Label>
                  </NavbarItem>

                  <NavbarSubItemGroup>
                    <NavbarItem
                      onClick={() => navigate(`/passport/${passportCountryCode}#offers`)}
                      active={
                        pathname === `/passport/${passportCountryCode}` || pathname === '/passport'
                      }
                    >
                      <Icon name="mortgageOffers" size="24" />
                      <Label variant="label-m">{t('Mortgage Offers')}</Label>
                    </NavbarItem>

                    <NavbarItem
                      onClick={() => navigate('/app/passport/book-mortgage-call')}
                      active={pathname === '/app/passport/book-mortgage-call'}
                    >
                      <PngIcon
                        src="/images/original/icons/book-mortgage-call.png"
                        alt=""
                        size={24}
                      />
                      <Label variant="label-m">{t('Book Mortgage Call')}</Label>
                    </NavbarItem>
                  </NavbarSubItemGroup>

                  <CollapsibleHeader
                    onClick={() => setPropertyFinderExpanded(!propertyFinderExpanded)}
                    active={pathname.startsWith('/app/property-finder')}
                  >
                    <CollapsibleHeaderContent>
                      <PngIcon
                        src="/images/original/icons/property-finder-v2.png"
                        alt=""
                        size={24}
                      />
                      <PropertyFinderWrapper>
                        <Label variant="label-m">{t('Properties')}</Label>
                      </PropertyFinderWrapper>
                    </CollapsibleHeaderContent>
                    <CollapseIcon $expanded={propertyFinderExpanded}>
                      <Icon name="chevron--down" size="16" />
                    </CollapseIcon>
                  </CollapsibleHeader>

                  {propertyFinderExpanded && (
                    <NavbarSubItemGroup>
                      <NavbarItem
                        onClick={() => navigate(`/app/property-finder/favourites`)}
                        active={pathname === `/app/property-finder/favourites`}
                      >
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill={pathname === `/app/property-finder/favourites` ? '#e74c3c' : 'none'}
                          stroke={
                            pathname === `/app/property-finder/favourites` ? '#e74c3c' : '#333'
                          }
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                        </svg>
                        <Label variant="label-m">{t('Favourites')}</Label>
                      </NavbarItem>
                      <NavbarItem
                        onClick={() => navigate(`/app/property-finder/past-searches`)}
                        active={
                          pathname === `/app/property-finder/past-searches` ||
                          pathname === `/app/property-finder/past-searches/add`
                        }
                      >
                        <Icon name="sparkle" size="24" />
                        <Label variant="label-m">{t('AI Searches')}</Label>
                      </NavbarItem>
                      <NavbarItem
                        onClick={() => navigate(`/app/property-finder/contact-agents`)}
                        active={pathname === `/app/property-finder/contact-agents`}
                        data-tour="contact-agents-nav"
                      >
                        <PngIcon
                          src="/images/original/icons/book-property-call.png"
                          alt=""
                          size={24}
                        />
                        <Label variant="label-m">{t('Book Property Call')}</Label>
                      </NavbarItem>
                      <NavbarItem
                        onClick={() => navigate(`/app/property-finder/scheduled-viewings`)}
                        active={pathname === `/app/property-finder/scheduled-viewings`}
                      >
                        <Icon name="scheduled" size="24" />
                        <Label variant="label-m">{t('Scheduled Viewings')}</Label>
                      </NavbarItem>
                    </NavbarSubItemGroup>
                  )}

                  {isCountrySupported(passportCountry?.country_code) && (
                    <NavbarItem
                      onClick={() => navigate(`/country-services/${passportCountry.country_code}`)}
                      active={pathname.startsWith('/country-services')}
                    >
                      <Icon name="globe" size="24" />
                      <Label variant="label-m">{t('Country Services')}</Label>
                    </NavbarItem>
                  )}

                  <NavbarItem onClick={() => navigate('/fx')} active={pathname === '/fx'}>
                    <Icon name="fx" size="24" />
                    <Label variant="label-m">{t('Currency Conversion')}</Label>
                  </NavbarItem>
                </>
              ) : null}
              {isMobile ? (
                <>
                  <NavbarItem
                    onClick={() => {
                      setMobileMoreMenuVisible(false);
                      navigate(`/passport/${passportCountryCode}`);
                    }}
                    active={pathname === `/passport/${passportCountryCode}`}
                  >
                    <Icon name="mortgage" size="24" />
                    <Label
                      variant="label-m"
                      color={pathname === `/passport/${passportCountryCode}` ? '#2980b9' : null}
                    >
                      {t('Mortgages')}
                    </Label>
                  </NavbarItem>
                  <NavbarItem
                    onClick={() => {
                      setMobileMoreMenuVisible(false);
                      navigate(`/app/property-finder/favourites`);
                    }}
                    active={pathname.startsWith('/app/property-finder')}
                  >
                    <PngIcon src="/images/original/icons/property-finder-v2.png" alt="" size={24} />
                    <Label
                      variant="label-m"
                      color={pathname.startsWith('/app/property-finder') ? '#2980b9' : null}
                    >
                      {t('Properties')}
                    </Label>
                  </NavbarItem>
                  <NavbarItem
                    onClick={() => {
                      setMobileMoreMenuVisible(false);
                      navigate('/fx');
                    }}
                    active={pathname === '/fx'}
                  >
                    <Icon name="fx" size="24" />
                    <Label variant="label-m" color={pathname === '/fx' ? '#2980b9' : null}>
                      {t('Conversion')}
                    </Label>
                  </NavbarItem>
                  <NavbarItem
                    type="button"
                    onClick={() => setMobileMoreMenuVisible(!mobileMoreMenuVisible)}
                    data-qa="mobile-more-toggle"
                    aria-expanded={mobileMoreMenuVisible}
                    aria-controls="mobile-more-menu"
                    active={mobileMoreMenuVisible}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ color: mobileMoreMenuVisible ? '#2980b9' : 'currentColor' }}
                    >
                      <line
                        x1="4"
                        y1="6"
                        x2="20"
                        y2="6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      <line
                        x1="4"
                        y1="12"
                        x2="20"
                        y2="12"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      <line
                        x1="4"
                        y1="18"
                        x2="20"
                        y2="18"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                    <Label variant="label-m" color={mobileMoreMenuVisible ? '#2980b9' : null}>
                      {t('More')}
                    </Label>
                  </NavbarItem>
                </>
              ) : null}
            </>
          </NavbarItems>
        </Navbar>
        <Footer>
          <NavbarItem
            onClick={() => navigate('/passport/documents')}
            data-qa="sidebar-documents"
            data-testid="sidebar-documents"
            active={pathname === '/passport/documents'}
          >
            <Icon
              name={docusignDocuments?.length ? 'documents--new' : 'documents'}
              size="24"
              color={isMobile && pathname !== '/passport/documents' ? '#999CAD' : null}
            />
            <Label
              variant="label-m"
              color={isMobile && pathname !== '/passport/documents' ? 'grey-50' : null}
            >
              {t('Documents')}
            </Label>
          </NavbarItem>
          {isVerifyEuroAccountPageReady && isVerifyEuroAccountPageEnabled === true ? (
            <NavbarItem
              type="button"
              onClick={() => navigate('/verify-euro-account')}
              data-qa="sidebar-verify-euro-account"
              data-testid="sidebar-verify-euro-account"
              data-active={pathname === '/verify-euro-account'}
              aria-current={pathname === '/verify-euro-account' ? 'page' : undefined}
              active={pathname === '/verify-euro-account'}
            >
              <PngIcon
                src="/images/original/icons/money-bill-transfer-solid-full.svg"
                alt=""
                size={24}
                data-testid="verify-euro-account-navigation-icon"
              />
              <Label variant="label-m">{t('wiseAccountVerification.navigationLabel')}</Label>
            </NavbarItem>
          ) : null}
          <UserDetails>
            <UserDetailsAction onClick={() => navigate('/settings')}>
              <UserAvatar />
              <UserPersonalData>
                <Label variant="label-s">{userName}</Label>
                <Label variant="label-s" color="grey-50" elipsis>
                  {userEmail}
                </Label>
              </UserPersonalData>
            </UserDetailsAction>
            <LogoutWrapper onClick={() => onLogout()}>
              <Icon name="logout" size="18" />
            </LogoutWrapper>
          </UserDetails>
        </Footer>
      </Sidebar>
      {isMobile ? (
        <>
          <MobileMoreMenuOverlay
            $isVisible={mobileMoreMenuVisible}
            onClick={() => setMobileMoreMenuVisible(false)}
          />
          <MobileMoreMenu id="mobile-more-menu" $isVisible={mobileMoreMenuVisible}>
            <MobileMoreMenuItem
              type="button"
              onClick={() => {
                setMobileMoreMenuVisible(false);
                navigate('/app/property-finder/favourites');
              }}
              active={pathname === '/app/property-finder/favourites'}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill={pathname === '/app/property-finder/favourites' ? '#e74c3c' : 'none'}
                stroke={pathname === '/app/property-finder/favourites' ? '#e74c3c' : '#333'}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
              </svg>
              <Label variant="label-m">{t('Favourites')}</Label>
            </MobileMoreMenuItem>
            <MobileMoreMenuItem
              onClick={() => {
                setMobileMoreMenuVisible(false);
                navigate('/app/property-finder/past-searches');
              }}
              active={
                pathname === '/app/property-finder/past-searches' ||
                pathname === '/app/property-finder/past-searches/add'
              }
            >
              <Icon name="sparkle" size="24" />
              <Label variant="label-m">{t('AI Searches')}</Label>
            </MobileMoreMenuItem>
            <MobileMoreMenuItem
              onClick={() => {
                setMobileMoreMenuVisible(false);
                navigate('/app/property-finder/contact-agents');
              }}
              active={pathname === '/app/property-finder/contact-agents'}
            >
              <PngIcon src="/images/original/icons/book-property-call.png" alt="" size={24} />
              <Label variant="label-m">{t('Book Property Call')}</Label>
            </MobileMoreMenuItem>
            <MobileMoreMenuItem
              onClick={() => {
                setMobileMoreMenuVisible(false);
                navigate('/app/property-finder/scheduled-viewings');
              }}
              active={pathname === '/app/property-finder/scheduled-viewings'}
            >
              <Icon name="scheduled" size="24" />
              <Label variant="label-m">{t('Scheduled Viewings')}</Label>
            </MobileMoreMenuItem>
            <MobileMoreMenuItem
              onClick={() => {
                setMobileMoreMenuVisible(false);
                navigate('/passport/documents');
              }}
              data-qa="mobile-more-documents"
              data-testid="mobile-more-documents"
              active={pathname === '/passport/documents'}
            >
              <Icon name={docusignDocuments?.length ? 'documents--new' : 'documents'} size="24" />
              <Label variant="label-m">{t('Documents')}</Label>
            </MobileMoreMenuItem>
            {isVerifyEuroAccountPageReady && isVerifyEuroAccountPageEnabled === true ? (
              <MobileMoreMenuItem
                type="button"
                onClick={() => {
                  setMobileMoreMenuVisible(false);
                  navigate('/verify-euro-account');
                }}
                data-qa="mobile-more-verify-euro-account"
                data-testid="mobile-more-verify-euro-account"
                data-active={pathname === '/verify-euro-account'}
                aria-current={pathname === '/verify-euro-account' ? 'page' : undefined}
                active={pathname === '/verify-euro-account'}
              >
                <PngIcon
                  src="/images/original/icons/money-bill-transfer-solid-full.svg"
                  alt=""
                  size={24}
                  data-testid="verify-euro-account-navigation-icon"
                />
                <Label variant="label-m">{t('wiseAccountVerification.navigationLabel')}</Label>
              </MobileMoreMenuItem>
            ) : null}
            <MobileMoreMenuItem
              onClick={() => {
                setMobileMoreMenuVisible(false);
                navigate('/settings');
              }}
              active={pathname === '/settings'}
            >
              <Icon name="license--third-partyFinancial profile" size="24" />
              <Label variant="label-m">{t('Profile')}</Label>
            </MobileMoreMenuItem>
            <MobileMoreMenuItem onClick={() => onLogout()}>
              <Icon name="logout" size="24" />
              <Label variant="label-m">{t('Logout')}</Label>
            </MobileMoreMenuItem>
          </MobileMoreMenu>
        </>
      ) : null}
    </>
  );
};

const PersistentSidebar = ({ blurred = false }) => {
  return <SidebarContent blurred={blurred} />;
};

export default PersistentSidebar;
